import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import Navbar from '../../components/navbar/navbar';
import Footer from '../../components/footer';
import { API_BASE, getImageUrl } from '../../utils/api';
import { useCity } from '../../context/CityContext';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";

interface Developer {
    _id: string;
    name: string;
    avatar: string;
    logo?: string;
    email?: string;
    phone?: string;
    city?: string;
    about?: string;
    description?: string;
    projectsCount?: number;
    projectCities?: string[];
}

const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const cardVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5,
            ease: "easeOut"
        }
    }
};

export default function DeveloperList() {
    const [developers, setDevelopers] = useState<Developer[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const { selectedCity } = useCity();
    const location = useLocation();
    const tagParam = new URLSearchParams(location.search).get('tag');

    useEffect(() => {
        const fetchDevelopers = async () => {
            try {
                const response = await fetch(`${API_BASE}/developers`);
                const data = await response.json();
                if (Array.isArray(data)) {
                    setDevelopers(data);
                }
            } catch (error) {
                console.error('Error fetching developers:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDevelopers();
    }, []);

    const filteredDevelopers = developers.filter(dev => {
        const matchesSearch = dev.name.toLowerCase().includes(searchTerm.toLowerCase());

        let matchesCity = true;
        if (selectedCity && selectedCity !== 'All Cities' && selectedCity !== 'Detecting Location...') {
            const cityLower = selectedCity.trim().toLowerCase();
            const ownCityMatch = dev.city ? dev.city.trim().toLowerCase() === cityLower : false;
            const projectCityMatch = dev.projectCities ? dev.projectCities.some(
                c => c.trim().toLowerCase() === cityLower
            ) : false;
            matchesCity = ownCityMatch || projectCityMatch;
        }

        return matchesSearch && matchesCity;
    });

    const sliderSettings = {
        dots: true,
        infinite: filteredDevelopers.length > 1,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        centerMode: true,
        centerPadding: '20px',
        autoplay: true,
        autoplaySpeed: 3000,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    centerPadding: '20px'
                }
            }
        ]
    };

    return (
        <div className="min-h-screen bg-[#f8f9ff] font-['Figtree']">
            <Navbar transparent={false} />

            {/* Premium Hero Section */}
            <div className="relative pt-32 pb-20 lg:pt-48 lg:pb-40 bg-gradient-to-br from-[#05144b] to-[#4c1d95] overflow-hidden">
                {/* Abstract Background Elements */}
                <div className="absolute inset-0">
                    <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-accent/10 rounded-full blur-[120px] animate-pulse"></div>
                    <div className="absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] bg-blue-400/5 rounded-full blur-[100px]"></div>
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.03]"></div>
                </div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-4xl">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-[2px] bg-accent"></div>
                                <span className="text-accent text-xs font-black">Elite Registry</span>
                            </div>
                            <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-8 tracking-tighter">
                                World-Class <span className="text-accent italic">Developers</span>
                            </h1>
                            <p className="text-white/70 text-lg md:text-xl max-w-2xl leading-relaxed font-medium mb-12">
                                Partnering with India's most visionary builders to deliver extraordinary living experiences and investment opportunities.
                            </p>

                            {/* Integrated Search Bar */}
                            <div className="relative max-w-2xl group">
                                <div className="absolute inset-0 bg-white/5 backdrop-blur-xl rounded-full -m-1 opacity-0 group-focus-within:opacity-100 transition-opacity duration-500"></div>
                                <div className="relative flex items-center bg-white/10 backdrop-blur-md border border-white/20 rounded-full p-2 transition-all duration-300 group-focus-within:border-accent">
                                    <div className="flex items-center flex-grow px-4">
                                        <i className="fa-solid fa-magnifying-glass text-white/40 group-focus-within:text-accent transition-colors"></i>
                                        <input
                                            type="text"
                                            className="w-full bg-transparent border-0 focus:ring-0 text-white font-bold px-4 py-3 placeholder:text-white/40"
                                            placeholder="Search by builder name..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <button className="hidden md:flex items-center gap-2 bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white font-black text-[11px] px-8 py-3.5 rounded-full transition-all shadow-xl">
                                        Search <i className="fa-solid fa-arrow-right-long"></i>
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Stats Badge */}
                <div className="hidden lg:flex absolute bottom-12 right-20 items-center gap-8 text-white/30">
                    <div className="text-right">
                        <div className="text-5xl font-black text-white/10 tracking-tighter leading-none mb-1">{developers.length}</div>
                        <div className="text-[10px] font-black">Verified Partners</div>
                    </div>
                    <div className="w-[1px] h-12 bg-white/10"></div>
                    <div className="text-right">
                        <div className="text-5xl font-black text-white/10 tracking-tighter leading-none mb-1">100%</div>
                        <div className="text-[10px] font-black">Quality Assured</div>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <section className="py-24 relative z-20">
                <div className="container mx-auto px-4">
                    {/* Filter Status Bar */}
                    <div className="flex flex-col md:flex-row items-center justify-between mb-12 pb-8 border-b border-gray-100 gap-6">
                        <div className="text-center md:text-left">
                            <h2 className="text-2xl font-black text-black tracking-tight">
                                {filteredDevelopers.length} <span className="text-gray-400 font-bold">Found</span>
                            </h2>
                            {selectedCity && selectedCity !== 'All Cities' && (
                                <div className="flex items-center gap-2 mt-1 text-sm font-bold text-accent">
                                    <i className="fa-solid fa-location-dot"></i>
                                    <span>Active in {selectedCity}</span>
                                </div>
                            )}
                        </div>
                        
                        {searchTerm && (
                            <button 
                                onClick={() => setSearchTerm('')}
                                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-full text-xs font-black hover:bg-red-600 hover:text-white transition-all"
                            >
                                <i className="fa-solid fa-xmark"></i> Clear Filters
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-32">
                            <div className="w-16 h-16 border-4 border-black border-t-accent rounded-full animate-spin mb-6"></div>
                            <p className="text-black font-black text-sm animate-pulse">Scanning Elite Registry...</p>
                        </div>
                    ) : (
                        <AnimatePresence mode="wait">
                            {filteredDevelopers.length > 0 ? (
                                <motion.div
                                    key="grid"
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
                                >
                                    {filteredDevelopers.map((dev, index) => (
                                        <motion.div key={dev._id} variants={cardVariants}>
                                            <DeveloperCard dev={dev} />
                                        </motion.div>
                                    ))}
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="empty"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="text-center py-32 bg-white rounded-[3rem] border border-gray-100 shadow-sm"
                                >
                                    <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-8">
                                        <i className="fa-solid fa-building-circle-xmark text-4xl text-gray-200"></i>
                                    </div>
                                    <h3 className="text-2xl font-black text-black mb-2">No Builders Found</h3>
                                    <p className="text-gray-500 font-medium max-w-xs mx-auto mb-8">We couldn't find any builders matching your current search criteria.</p>
                                    <button 
                                        onClick={() => setSearchTerm('')}
                                        className="px-8 py-3 bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white rounded-2xl font-black text-xs shadow-xl hover:brightness-110 transition-all"
                                    >
                                        View All Builders
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    )}
                </div>
            </section>

            <Footer />

            <style>{`
                .developer-mobile-slider .slick-dots { bottom: -40px; }
                .developer-mobile-slider .slick-dots li button:before { color: #000000; font-size: 8px; opacity: 0.2; }
                .developer-mobile-slider .slick-dots li.slick-active button:before { color: #f1ae24; opacity: 1; }
            `}</style>
        </div>
    );
}

function DeveloperCard({ dev }: { dev: Developer }) {
    return (
        <Link to={`/developer-page/${dev._id}`} className="block group">
            <div className="relative bg-white rounded-[2.5rem] border border-gray-100 p-8 pt-12 shadow-[0_10px_40px_-20px_rgba(0,0,0,0.05)] hover:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.1)] transition-all duration-500 group-hover:-translate-y-2 h-full flex flex-col items-center text-center overflow-hidden">
                {/* Accent Background Element */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gray-50 rounded-bl-[4rem] -mr-8 -mt-8 group-hover:bg-accent/5 transition-colors duration-500"></div>
                
                {/* Logo Section */}
                <div className="relative mb-8">
                    <div className="absolute inset-0 bg-accent/20 blur-2xl rounded-full scale-0 group-hover:scale-100 transition-transform duration-500"></div>
                    <div className="relative w-28 h-28 rounded-3xl bg-white p-4 shadow-xl border border-gray-50 flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                        <img
                            src={getImageUrl(dev.avatar || dev.logo || '/default-builder.png')}
                            alt={dev.name}
                            className="w-full h-full object-contain filter group-hover:brightness-110"
                            onError={(e: any) => { e.target.src = '/default-builder.png'; }}
                        />
                    </div>
                    {/* Status Dot */}
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full border-4 border-white shadow-lg animate-pulse"></div>
                </div>

                {/* Content Section */}
                <div className="flex-grow flex flex-col">
                    <h3 className="text-xl font-black text-black mb-2 tracking-tight line-clamp-1 group-hover:text-accent transition-colors">
                        {dev.name}
                    </h3>
                    <div className="flex items-center justify-center gap-2 mb-4 text-[10px] font-black text-gray-500">
                        <i className="fa-solid fa-location-dot text-accent"></i>
                        {dev.city || 'National Developer'}
                    </div>
                    <p className="text-sm font-medium text-gray-600 leading-relaxed mb-6 line-clamp-3">
                        {dev.description || 'Pioneering innovative architecture and premium lifestyle developments with a focus on quality and timely delivery.'}
                    </p>
                </div>

                {/* Card Footer */}
                <div className="w-full pt-6 mt-auto border-t border-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                        <span className="text-[10px] font-black text-black">Premium Partner</span>
                    </div>
                    <div className="w-10 h-10 rounded-2xl bg-gray-50 flex items-center justify-center text-black group-hover:bg-gradient-to-r group-hover:from-[#05144b] group-hover:to-[#4c1d95] group-hover:text-white transition-all duration-300">
                        <i className="fa-solid fa-arrow-right-long"></i>
                    </div>
                </div>
            </div>
        </Link>
    );
}
